import { Timestamp } from 'firebase/firestore';

/**
 * Card condition grades following industry standards
 */
export type CardCondition =
  | 'mint'
  | 'near_mint'
  | 'lightly_played'
  | 'moderately_played'
  | 'heavily_played'
  | 'damaged';

/**
 * Card variant types for Pokemon TCG
 */
export type CardVariant =
  | 'normal'
  | 'holofoil'
  | 'reverse_holofoil'
  | 'full_art'
  | 'special_art';

/**
 * A single instance of a card in the user's collection
 * Users can have multiple instances of the same card (different conditions, variants, etc.)
 */
export interface CardInstance {
  /** Unique instance ID (Firestore document ID) */
  id?: string;
  /** Card ID from the Pokemon TCG API (e.g., "sv3pt5-1") */
  cardId: string;
  /** Physical condition of the card */
  condition: CardCondition;
  /** Variant type (normal, holo, etc.) */
  variant: CardVariant;
  /** Quantity of this exact card/condition/variant combination */
  quantity: number;
  /** When the card was added to collection */
  acquiredAt: Timestamp | Date;
  /** Price paid for the card (in user's currency) */
  acquiredPrice?: number;
  /** Whether this card is available for trade */
  forTrade: boolean;
  /** Optional user notes about this card */
  notes?: string;
}

/**
 * Data needed to create a new card instance (without auto-generated fields)
 */
export interface CreateCardInstanceData {
  cardId: string;
  condition: CardCondition;
  variant: CardVariant;
  quantity: number;
  acquiredPrice?: number;
  forTrade?: boolean;
  notes?: string;
}

/**
 * Data for updating an existing card instance
 */
export interface UpdateCardInstanceData {
  condition?: CardCondition;
  variant?: CardVariant;
  quantity?: number;
  acquiredPrice?: number;
  forTrade?: boolean;
  notes?: string;
}

/**
 * Price history snapshot for a card
 */
export interface PriceSnapshot {
  /** Date in YYYY-MM-DD format */
  date: string;
  /** Average selling price */
  averageSellPrice: number;
  /** Lowest available price */
  lowPrice: number;
  /** Market trend price */
  trendPrice: number;
}

/**
 * Exchange profile for the card trading system
 */
export interface ExchangeProfile {
  /** User ID */
  uid: string;
  /** User's display handle */
  handle: string;
  /** User's location for local trades */
  location?: {
    country: string;
    city?: string;
  };
  /** Card IDs available for trade (denormalized for querying) */
  forTrade: string[];
  /** Card IDs the user is seeking */
  seeking: string[];
  /** Last activity timestamp */
  lastActive: Timestamp | Date;
  /** Trade preferences */
  preferences?: {
    shippingAvailable?: boolean;
    localOnly?: boolean;
    minTradeValue?: number;
  };
}

/**
 * A potential trade match between two users
 */
export interface TradeMatch {
  /** The other user's profile */
  user: ExchangeProfile;
  /** Cards they have that you want */
  theyHaveYouWant: string[];
  /** Cards you have that they want */
  youHaveTheyWant: string[];
  /** Match score (higher = better mutual match) */
  score: number;
}

/**
 * Collection statistics for dashboard
 */
export interface CollectionStats {
  /** Total number of unique cards */
  uniqueCards: number;
  /** Total number of card instances (including duplicates) */
  totalCards: number;
  /** Estimated total value */
  totalValue: number;
  /** Value change in last 24 hours */
  valueChange24h: number;
  /** Value change percentage */
  valueChangePercent: number;
  /** Number of sets with at least one card */
  setsStarted: number;
  /** Number of completed sets */
  setsCompleted: number;
  /** Cards available for trade */
  cardsForTrade: number;
  /** Cards on wanted list */
  cardsWanted: number;
}

/**
 * Condition display information
 */
export const CONDITION_INFO: Record<CardCondition, { label: string; abbrev: string; color: string; description: string }> = {
  mint: {
    label: 'Mint',
    abbrev: 'M',
    color: '#22C55E',
    description: 'Perfect condition, no visible wear'
  },
  near_mint: {
    label: 'Near Mint',
    abbrev: 'NM',
    color: '#84CC16',
    description: 'Minor imperfections only visible on close inspection'
  },
  lightly_played: {
    label: 'Lightly Played',
    abbrev: 'LP',
    color: '#EAB308',
    description: 'Some wear visible, but overall good condition'
  },
  moderately_played: {
    label: 'Moderately Played',
    abbrev: 'MP',
    color: '#F97316',
    description: 'Moderate wear, visible scratches or edge wear'
  },
  heavily_played: {
    label: 'Heavily Played',
    abbrev: 'HP',
    color: '#EF4444',
    description: 'Significant wear, creases or heavy edge damage'
  },
  damaged: {
    label: 'Damaged',
    abbrev: 'D',
    color: '#991B1B',
    description: 'Major damage affecting card integrity'
  }
};

/**
 * Variant display information
 */
export const VARIANT_INFO: Record<CardVariant, { label: string; icon: string }> = {
  normal: { label: 'Normal', icon: '⬜' },
  holofoil: { label: 'Holofoil', icon: '✨' },
  reverse_holofoil: { label: 'Reverse Holo', icon: '🔄' },
  full_art: { label: 'Full Art', icon: '🎨' },
  special_art: { label: 'Special Art', icon: '⭐' }
};
